/**
 * Google appends each Autocomplete's dropdown (`.pac-container`) to
 * document.body some time AFTER `new Autocomplete(...)` (on first use), so
 * a field cannot find its own dropdown by looking right after creating it.
 *
 * Each field waits here from just before it creates its Autocomplete. When
 * a new `.pac-container` lands on body it goes to the waiting field that has
 * focus (Google builds it when that field is used), or to the only waiting
 * field. With several fields waiting and none focused, nobody can tell whose
 * it is, so it stays unclaimed: a field never checks, or removes, another
 * field's dropdown. A claimed container is marked so no other field takes
 * it.
 */
type Waiter = {
  node: HTMLInputElement;
  claim: (container: HTMLElement) => void;
};

const waiting: Waiter[] = [];
let observer: MutationObserver | undefined;

const stopIfIdle = () => {
  if (!waiting.length) {
    observer?.disconnect();
    observer = undefined;
  }
};

const assign = (container: HTMLElement) => {
  if (!waiting.length || container.dataset.clixPacClaimed === 'true') {
    return;
  }
  const focused = waiting.findIndex(
    (waiter) => waiter.node === document.activeElement
  );
  const index = focused >= 0 ? focused : waiting.length === 1 ? 0 : -1;
  if (index < 0) {
    return;
  }
  const [waiter] = waiting.splice(index, 1);
  container.dataset.clixPacClaimed = 'true';
  stopIfIdle();
  waiter.claim(container);
};

export const awaitPacContainer = (
  node: HTMLInputElement,
  claim: (container: HTMLElement) => void
) => {
  const waiter = { node, claim };
  waiting.push(waiter);
  if (!observer && typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver((records) =>
      records.forEach((record) =>
        record.addedNodes.forEach((added) => {
          if (
            added instanceof HTMLElement &&
            added.classList.contains('pac-container')
          ) {
            assign(added);
          }
        })
      )
    );
    observer.observe(document.body, { childList: true });
  }
  return () => {
    const index = waiting.indexOf(waiter);
    if (index >= 0) {
      waiting.splice(index, 1);
    }
    stopIfIdle();
  };
};

/** Test-only: is the body watcher running? */
export const pacWatcherActiveForTests = () => Boolean(observer);
